import * as React from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, Space, Spin } from 'antd';
import { ApplicationDefinitionBase } from '../../../iota_applications/types/ApplicationDefinition';
import { Script } from '../../../script_management/types';
import { SelectWhenScroll } from '../SelectWhenScroll';
import {
    ClientTaxonomyCollectionKey,
    ClientTaxonomyPipelineStep,
    ClientTaxonomyReferenceCollection,
    ClientTaxonomyRefreshPeriod,
    PipelineStepFieldValue
} from './CustomBlock';
import { isValidClientTaxonomyCollectionKey } from './ClientTaxonomyCollection';

const hasNonEmptyStringSetting = (value: unknown): value is string => {
    return typeof value === 'string' && value.trim().length > 0;
};

const hasRequiredClientTaxonomyFlowSettings = (application: ApplicationDefinitionBase): boolean => {
    return (
        hasNonEmptyStringSetting(application.settingValues?.Endpoint) &&
        hasNonEmptyStringSetting(application.settingValues?.EnvironmentId)
    );
};

export const CLIENT_TAXONOMY_DATA_SET_OPTIONS: Array<{
    label: string;
    value: ClientTaxonomyCollectionKey;
}> = [
    { label: 'Business Classification', value: 'business_classification' },
    { label: 'Country Groups', value: 'country_groups' },
    { label: 'Entry Codes', value: 'entry_codes' }
];

export const CLIENT_TAXONOMY_REFRESH_PERIOD_OPTIONS: Array<{
    label: string;
    value: ClientTaxonomyRefreshPeriod;
}> = [
    { label: '60 minutes', value: 'PT1H' },
    { label: '1 day', value: 'P1D' },
    { label: '1 week', value: 'P7D' }
];

interface ClientTaxonomyStepConfigProps {
    step: ClientTaxonomyPipelineStep;
    applicationDefinitions: ApplicationDefinitionBase[];
    scripts: Script[];
    isLoadingApplications: boolean;
    isLoadingScripts: boolean;
    disabled: boolean;
    onFieldChange: (field: string, value: PipelineStepFieldValue) => void;
}

const ClientTaxonomyStepConfig: React.FC<ClientTaxonomyStepConfigProps> = ({
    step,
    applicationDefinitions,
    scripts,
    isLoadingApplications,
    isLoadingScripts,
    disabled,
    onFieldChange
}) => {
    const applicationOptions = React.useMemo(
        () =>
            applicationDefinitions
                .filter(hasRequiredClientTaxonomyFlowSettings)
                .map(application => ({ label: application.name, value: application.id })),
        [applicationDefinitions]
    );

    const scriptOptions = React.useMemo(
        () => scripts.map(script => ({ label: script.name, value: script.id })),
        [scripts]
    );

    const configuredCollections = step.reference_data?.collections;
    const collections = Array.isArray(configuredCollections)
        ? configuredCollections.filter(
              collection => collection && typeof collection === 'object' && !Array.isArray(collection)
          )
        : [];
    const selectedCollectionKeys = new Set(collections.map(collection => collection.data_set).filter(Boolean));
    const collectionKeyCounts = collections.reduce<Record<string, number>>((counts, collection) => {
        if (collection.data_set) {
            counts[collection.data_set] = (counts[collection.data_set] ?? 0) + 1;
        }
        return counts;
    }, {});
    const nextAvailableDataSet = CLIENT_TAXONOMY_DATA_SET_OPTIONS.find(
        option => !selectedCollectionKeys.has(option.value)
    );
    const isFilled = (value: unknown) => typeof value === 'string' && value.trim().length > 0;
    const hasIncompleteCollection = collections.some(
        collection =>
            !isValidClientTaxonomyCollectionKey(collection.data_set) ||
            !isFilled(collection.script_id) ||
            collectionKeyCounts[collection.data_set] > 1
    );

    const updateCollections = (updatedCollections: ClientTaxonomyReferenceCollection[]) => {
        onFieldChange('reference_data', {
            collections: updatedCollections
        });
    };

    const updateCollection = <TField extends keyof ClientTaxonomyReferenceCollection>(
        index: number,
        field: TField,
        value: ClientTaxonomyReferenceCollection[TField]
    ) => {
        updateCollections(
            collections.map((collection, collectionIndex) =>
                collectionIndex === index ? { ...collection, [field]: value } : collection
            )
        );
    };

    const addCollection = () => {
        if (!nextAvailableDataSet) {
            return;
        }

        updateCollections([
            ...collections,
            {
                data_set: nextAvailableDataSet.value,
                script_id: ''
            }
        ]);
    };

    const removeCollection = (index: number) => {
        updateCollections(collections.filter((_, collectionIndex) => collectionIndex !== index));
    };

    const showRequiredError = (value: unknown) => !disabled && !isFilled(value);

    return (
        <Space className="client-taxonomy-step-config" direction="vertical" style={{ width: '100%' }} size="small">
            <div className="custom-block-field">
                <label className="custom-block-label required">Application</label>
                <Form.Item
                    validateStatus={showRequiredError(step.application_id) ? 'error' : ''}
                    style={{ marginBottom: 0 }}
                >
                    <SelectWhenScroll
                        aria-label="Application"
                        value={step.application_id}
                        onChange={value => onFieldChange('application_id', value)}
                        options={applicationOptions}
                        disabled={disabled || isLoadingApplications}
                        loading={isLoadingApplications}
                        notFoundContent={
                            isLoadingApplications ? (
                                <Spin size="small" />
                            ) : (
                                'No applications with required FLOW settings found'
                            )
                        }
                        showSearch
                        optionFilterProp="label"
                        placeholder="Select an application"
                        allowClear
                        style={{ width: '100%' }}
                    />
                </Form.Item>
                {showRequiredError(step.application_id) && (
                    <span className="custom-block-validation-error">Application is required.</span>
                )}
            </div>

            <div className="reference-data-section">
                <div className="reference-data-header">
                    <span className="custom-block-label required">Reference Data</span>
                    <Button
                        aria-label="Add Reference Data collection"
                        icon={<PlusOutlined />}
                        size="small"
                        disabled={disabled || !nextAvailableDataSet || hasIncompleteCollection}
                        onClick={addCollection}
                    >
                        Add Collection
                    </Button>
                </div>

                {collections.length === 0 && !disabled && (
                    <span className="custom-block-validation-error">
                        At least one Reference Data collection is required.
                    </span>
                )}

                {collections.map((collection, index) => (
                    <div className="reference-data-collection" key={index}>
                        <div className="reference-data-fields">
                            <div className="custom-block-field">
                                <label className="custom-block-label required">Data Set</label>
                                <Form.Item
                                    validateStatus={
                                        !disabled &&
                                        (!isValidClientTaxonomyCollectionKey(collection.data_set) ||
                                            collectionKeyCounts[collection.data_set] > 1)
                                            ? 'error'
                                            : ''
                                    }
                                    style={{ marginBottom: 0 }}
                                >
                                    <SelectWhenScroll
                                        aria-label={`Data Set ${index + 1}`}
                                        value={collection.data_set}
                                        onChange={value => updateCollection(index, 'data_set', value)}
                                        options={CLIENT_TAXONOMY_DATA_SET_OPTIONS.filter(
                                            option =>
                                                option.value === collection.data_set ||
                                                !selectedCollectionKeys.has(option.value)
                                        )}
                                        disabled={disabled}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                                {showRequiredError(collection.data_set) && (
                                    <span className="custom-block-validation-error">Data Set is required.</span>
                                )}
                                {collection.data_set &&
                                    !isValidClientTaxonomyCollectionKey(collection.data_set) &&
                                    !disabled && (
                                        <span className="custom-block-validation-error">
                                            Data Set must be a lower-snake-case key of at most 64 characters.
                                        </span>
                                    )}
                                {collection.data_set && collectionKeyCounts[collection.data_set] > 1 && !disabled && (
                                    <span className="custom-block-validation-error">Data Set must be unique.</span>
                                )}
                            </div>

                            <div className="custom-block-field">
                                <label className="custom-block-label required">Script</label>
                                <Form.Item
                                    validateStatus={showRequiredError(collection.script_id) ? 'error' : ''}
                                    style={{ marginBottom: 0 }}
                                >
                                    <SelectWhenScroll
                                        aria-label={`Script ${index + 1}`}
                                        value={collection.script_id}
                                        onChange={value => updateCollection(index, 'script_id', value || '')}
                                        options={scriptOptions}
                                        disabled={disabled || isLoadingScripts}
                                        loading={isLoadingScripts}
                                        notFoundContent={isLoadingScripts ? <Spin size="small" /> : 'No scripts found'}
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Select a script"
                                        allowClear
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                                {showRequiredError(collection.script_id) && (
                                    <span className="custom-block-validation-error">Script is required.</span>
                                )}
                            </div>
                        </div>

                        <Button
                            aria-label={`Remove Reference Data collection ${index + 1}`}
                            className="reference-data-remove-button"
                            icon={<DeleteOutlined />}
                            size="small"
                            disabled={disabled}
                            onClick={() => removeCollection(index)}
                        />
                    </div>
                ))}
            </div>

            <div className="custom-block-field">
                <Checkbox
                    checked={step.use_cached_data ?? true}
                    onChange={event => onFieldChange('use_cached_data', event.target.checked)}
                    disabled={disabled}
                >
                    Use cached data
                </Checkbox>
            </div>

            <div className="custom-block-field">
                <label className="custom-block-label">Refresh Period</label>
                <Form.Item
                    validateStatus={
                        !disabled &&
                        !CLIENT_TAXONOMY_REFRESH_PERIOD_OPTIONS.some(option => option.value === step.refresh_period)
                            ? 'error'
                            : ''
                    }
                    style={{ marginBottom: 0 }}
                >
                    <SelectWhenScroll
                        aria-label="Refresh Period"
                        value={step.refresh_period || 'PT1H'}
                        onChange={value => onFieldChange('refresh_period', value)}
                        options={CLIENT_TAXONOMY_REFRESH_PERIOD_OPTIONS}
                        disabled={disabled || !(step.use_cached_data ?? true)}
                        style={{ width: '100%' }}
                    />
                </Form.Item>
            </div>

            <div className="client-taxonomy-help-text">
                Adds ClientTaxonomy to the pipeline context. The preceding step result remains unchanged.
            </div>
        </Space>
    );
};

export default ClientTaxonomyStepConfig;
